import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, ChevronLeft, ChevronRight, Filter, Clock, Shield, FileText, User, RefreshCw } from 'lucide-react';
import { auditAPI } from '@/lib/api';

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  transfer: 'bg-purple-100 text-purple-700',
  login: 'bg-slate-100 text-slate-600',
  login_failed: 'bg-red-100 text-red-700',
  status_change: 'bg-amber-100 text-amber-700',
};

const ENTITY_ICONS = {
  agent: User,
  contract: FileText,
  user: User,
  invitation: FileText,
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [filters, setFilters] = useState({ actions: [], entity_types: [] });
  const pageSize = 25;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      const res = await auditAPI.getLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { /* skip */ }
    finally { setLoading(false); }
  }, [page, search, actionFilter, entityFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    auditAPI.getFilters().then(res => setFilters(res.data)).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / pageSize);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadLogs();
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">Compliance trail of all system actions</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by user, entity, details..."
                  className="pl-9"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="audit-search"
                />
              </div>
            </div>
            <Select value={actionFilter} onValueChange={v => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="audit-action-filter">
                <Filter size={14} className="mr-1.5 text-slate-400" />
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {filters.actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={v => { setEntityFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="audit-entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {filters.entity_types.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" size="sm" variant="outline" data-testid="audit-search-btn">
              <Search size={14} className="mr-1" /> Search
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setSearch(''); setActionFilter(''); setEntityFilter(''); setPage(1); }}>
              <RefreshCw size={14} className="mr-1" /> Reset
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Shield size={16} className="text-slate-400" />
              Activity ({total.toLocaleString()} entries)
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="w-[160px]">Timestamp</TableHead>
                  <TableHead className="w-[110px]">Action</TableHead>
                  <TableHead className="w-[110px]">Entity</TableHead>
                  <TableHead className="w-[120px]">Entity ID</TableHead>
                  <TableHead className="w-[180px]">User</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No audit logs found</TableCell></TableRow>
                ) : logs.map(log => {
                  const Icon = ENTITY_ICONS[log.entity_type] || FileText;
                  return (
                    <TableRow key={log.audit_id} data-testid={`audit-row-${log.audit_id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        <Clock size={12} className="inline mr-1" />
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                          <Icon size={12} />
                          {log.entity_type}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-700 max-w-[120px] truncate">{log.entity_id}</TableCell>
                      <TableCell className="text-xs text-slate-700 max-w-[180px] truncate">{log.performed_by}</TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[300px] truncate">{log.details || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
                  <ChevronLeft size={14} />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
